// hooks/useAutoSave.js
import { useEffect, useRef, useCallback, useState } from "react";

/**
 * Hook für Auto-Save mit Debouncing
 * Speichert Änderungen nach X ms automatisch
 * 
 * @param {Object} data - Zu speichernde Daten
 * @param {Function} onSave - Callback zur Speicherung (wird mit data aufgerufen)
 * @param {number} delay - Verzögerung in ms (default: 800ms)
 * @returns {Object} { isSaving, error, lastSaved }
 */
export function useAutoSave(data, onSave, delay = 800) {
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [lastSaved, setLastSaved] = useState(null);
  
  const timeoutRef = useRef(null);
  const previousDataRef = useRef(data);

  const save = useCallback(async () => {
    try {
      setIsSaving(true);
      setError(null);
      await onSave(data);
      setLastSaved(new Date());
      previousDataRef.current = { ...data };
    } catch (err) {
      setError(err.message || "Fehler beim Speichern");
    } finally {
      setIsSaving(false);
    }
  }, [data, onSave]);

  useEffect(() => {
    // Nur speichern wenn sich Daten wirklich geändert haben
    const dataChanged = JSON.stringify(previousDataRef.current) !== JSON.stringify(data);
    
    if (!dataChanged) return;

    // Alten Timer löschen
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Neuer Timer
    timeoutRef.current = setTimeout(() => {
      save();
    }, delay);

    // Cleanup: Timer löschen beim unmount oder bei neuem Effect
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [data, save, delay]);

  return { isSaving, error, lastSaved };
}
